import { Icon } from '../ui/Icon';
import { Card, ErrorPanel, LoadingPanel } from '../ui/primitives';
import { cx } from '../ui/primitives/cx';
import type { UserRole } from '../../types/auth';
import type { PendingSummary } from '../../types/dashboard';
import { PENDING_EMPTY_TEXT, PENDING_EMPTY_TITLE, PENDING_SUPPORT, PENDING_TITLE, pendingView, type PendingTarget } from '../../utils/inicioView';
import { CardHead, CountChip, DoneBox, Tile } from './InicioParts';
import type { SummaryState } from './InicioPulse';

const TITLE_ID = 'inicio-pending-title';

interface InicioPendingProps {
  data: PendingSummary | undefined;
  state: SummaryState;
  role: UserRole | undefined;
  onNavigate: (destination: string) => void;
  onRetry: () => void;
}

/**
 * Rediseño · Bloque C — «Por resolver» (decisión B0-06 del dueño).
 *
 * Es el panel de pendientes del Inicio, entero, con otro nombre: sustituye al bloque general
 * «Te toca / solo tú puedes decidir» del diseño (P-022). Cada persona ve lo que puede atender según
 * sus permisos — qué entra y cuánto lo decide el servidor —: dueño y gerente, sus aprobaciones y la
 * operación del día; un empleado, su lista de conteo y el corte de su tienda.
 *
 * NO sustituye «Te toca contar», que es el título de la pantalla de Conteos (F4) y sigue igual.
 *
 * Lo nuevo respecto al panel anterior es el nombre, la piel y dos estados que antes se callaban: si
 * el resumen no llega, se dice y se puede reintentar; y el «Todo al día» lo ve también el empleado.
 */
export function InicioPending({ data, state, role, onNavigate, onRetry }: InicioPendingProps) {
  const view = data ? pendingView(data, role) : null;

  const open = (target: PendingTarget) => {
    // P-075: «Supervisión de formularios» abre la pestaña Supervisión, no la Administración.
    if (target.kind === 'section') { onNavigate(target.view ? `${target.section}/${target.view}` : target.section); return; }
    // El descuento por ventas entra con su rango ya puesto desde la venta más vieja: sin esto había
    // que adivinarlo. Mismas llaves que usaba el panel anterior; la pantalla de destino no cambió.
    if (target.markEntry) {
      try {
        sessionStorage.setItem('n360.descuento.entrada-hoy', '1');
        if (target.from) sessionStorage.setItem('n360.descuento.desde', target.from);
      } catch { /* sin almacenamiento */ }
    }
    onNavigate('inventory/consumption');
  };

  return (
    <Card aria-labelledby={TITLE_ID} as="section">
      <CardHead
        aside={view?.total ? <CountChip count={view.total} label={view.total === 1 ? 'pendiente' : 'pendientes'} /> : undefined}
        id={TITLE_ID}
        title={PENDING_TITLE}
      />
      {!view && state === 'loading' && <LoadingPanel label="Cargando pendientes…" lines={3} />}
      {!view && state === 'error' && (
        <ErrorPanel description="Puede ser la conexión." onRetry={onRetry} plain title="No pude traer tus pendientes ahora." />
      )}
      {view && view.rows.length === 0 && <DoneBox text={PENDING_EMPTY_TEXT} title={PENDING_EMPTY_TITLE} />}
      {view && view.rows.length > 0 && (
        <>
          <p className="n3-inicio__support">{PENDING_SUPPORT}</p>
          <ul className="n3-inicio__rows">
            {view.rows.map((row) => (
              <li key={row.key}>
                <button className={cx('n3-inicio__row', row.isNotice && 'n3-inicio__row--notice')} onClick={() => open(row.target)} type="button">
                  <Tile icon={row.icon} tone={row.isNotice ? 'warning' : 'brand'} />
                  <span className="n3-inicio__row-body">
                    <strong className="n3-inicio__row-title">{row.title}</strong>
                    <span className="n3-inicio__row-detail">{row.detail}</span>
                  </span>
                  <span className="n3-inicio__row-end">
                    {row.count !== null && <span className="n3-inicio__row-count">{row.count}</span>}
                    <span className="n3-inicio__row-cta">{row.cta} <Icon name="chevron-right" size={15} /></span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </Card>
  );
}
